import { useState, type FormEvent } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import AppDialog from '../../../components/AppDialog';
import SpinnerButton from '../../../components/SpinnerButton';
import { useModalHistory } from '../../../utils/useModalHistory';
import { FAQ_MODAL_LABELS as LABELS } from '../../../utils/labels';
import { FAQ_CATEGORIES } from '../constants';
import type { FaqCategoryKey, FaqItem } from '../types';

interface FaqModalProps {
  item?: FaqItem;
  onClose: () => void;
  onSave: (categoryKey: FaqCategoryKey, question: string, answer: string) => Promise<boolean>;
}

export default function FaqModal({ item, onClose, onSave }: FaqModalProps) {
  useModalHistory(onClose);
  const [categoryKey, setCategoryKey] = useState<FaqCategoryKey | ''>(item?.category_key ?? '');
  const [question, setQuestion] = useState(item?.question ?? '');
  const [answer, setAnswer] = useState(item?.answer ?? '');
  const [categoryError, setCategoryError] = useState('');
  const [questionError, setQuestionError] = useState('');
  const [answerError, setAnswerError] = useState('');
  const [saving, setSaving] = useState(false);

  const isCreate = !item;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    let hasError = false;
    if (!categoryKey) {
      setCategoryError(LABELS.categoryRequired);
      hasError = true;
    }
    if (!question.trim()) {
      setQuestionError(LABELS.questionRequired);
      hasError = true;
    }
    if (!answer.trim()) {
      setAnswerError(LABELS.answerRequired);
      hasError = true;
    }
    if (hasError || !categoryKey) return;

    setSaving(true);
    const ok = await onSave(categoryKey, question.trim(), answer.trim());
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <AppDialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={isCreate ? LABELS.createTitle : LABELS.editTitle}
      confirmLabel={LABELS.save}
      confirmFormId="faq-form"
      confirmDisabled={saving}
      confirmLoading={saving}
      testId="faq-modal"
    >
      <form id="faq-form" onSubmit={handleSubmit} noValidate>
        <DialogContent className="flex flex-col gap-4">
          <FormControl fullWidth error={!!categoryError} className="mt-2">
            <InputLabel>{LABELS.categoryLabel}</InputLabel>
            <Select
              label={LABELS.categoryLabel}
              value={categoryKey}
              onChange={(e) => {
                setCategoryKey(e.target.value as FaqCategoryKey);
                if (categoryError) setCategoryError('');
              }}
              inputProps={{ 'aria-label': LABELS.categoryLabel }}
            >
              {FAQ_CATEGORIES.map((c) => (
                <MenuItem key={c.key} value={c.key} data-testid={`option-${c.key}`}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
            {categoryError && <FormHelperText>{categoryError}</FormHelperText>}
          </FormControl>
          <TextField
            label={LABELS.questionLabel}
            value={question}
            onChange={(e) => {
              setQuestion(e.target.value);
              if (questionError) setQuestionError('');
            }}
            required
            fullWidth
            error={!!questionError}
            helperText={questionError || undefined}
          />
          <TextField
            label={LABELS.answerLabel}
            value={answer}
            onChange={(e) => {
              setAnswer(e.target.value);
              if (answerError) setAnswerError('');
            }}
            required
            fullWidth
            multiline
            minRows={5}
            error={!!answerError}
            helperText={answerError || undefined}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">
            {LABELS.cancel}
          </Button>
          <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">
            {LABELS.save}
          </SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
